<template>
  <div class="contents">
    <v-autocomplete
      v-model="filterStates.sectorIds"
      :items="sectorFilterOptions"
      label="Setor do plantão"
      multiple
      clearable
      theme="light"
      variant="outlined"
      density="compact"
      class="w-full"
      color="primary20"
      :disabled="!filterStates.healthUnits?.length"
      :hint="filterStates.healthUnits?.length ? '' : 'Escolha a unidade para filtrar por setor'"
      persistent-hint
      data-testid="consult-filter-sector-select"
    />
    <v-autocomplete
      v-model="specialtyFilterValue"
      :items="specialtyFilterOptions"
      label="Especialidade do plantão"
      multiple
      clearable
      theme="light"
      variant="outlined"
      density="compact"
      class="w-full"
      color="primary20"
      :disabled="!filterStates.company"
      :hint="filterStates.company ? '' : 'Escolha a empresa para filtrar por especialidade'"
      persistent-hint
      data-testid="consult-filter-specialty-select"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { filterStates } from './useFilterStation'
import { useSectorStore } from '@/pinia-store/useSectorStore'
import { useSpecialtyStore } from '@/pinia-store/useSpecialtyStore'
import { catalogDisplayName } from '@/utils/stationDisplay'

/**
 * Setor e especialidade DO PLANTÃO no modal de filtros da Consulta (spec §4.7).
 * Escreve direto no `filterStates` compartilhado, como o resto do modal.
 */
const sectorStore = useSectorStore()
const specialtyStore = useSpecialtyStore()

const unitIds = (): number[] => (filterStates.healthUnits ?? []).map(Number)

watch(
  unitIds,
  (units, previous) => {
    units.forEach((id) => void sectorStore.fetchByHealthUnit(id))
    // Unidade desmarcada: os setores dela sairiam das opções mas ficariam selecionados (invisíveis).
    const removed = (previous ?? []).filter((id) => !units.includes(id))
    if (!removed.length || !filterStates.sectorIds?.length) return
    const orphan = new Set(removed.flatMap((id) => sectorStore.sectorsOf(id).map((s) => s.id)))
    const kept = filterStates.sectorIds.filter((id) => !orphan.has(Number(id)))
    if (kept.length !== filterStates.sectorIds.length)
      filterStates.sectorIds = kept.length ? kept : undefined
  },
  { immediate: true }
)

watch(
  () => [filterStates.company, filterStates.companySpecialtyIds] as const,
  ([company, ids], previous) => {
    if (company) void specialtyStore.fetchOptions(Number(company))
    if (!previous) return
    const [previousCompany, previousIds] = previous
    // Troca de empresa feita pelo usuário: a especialidade DA EMPRESA não vale na outra (as
    // oficiais ficam). Se os ids mudaram junto (reidratação da URL), a troca não é do usuário.
    if (previousCompany && company !== previousCompany && ids === previousIds && ids?.length)
      filterStates.companySpecialtyIds = undefined
  },
  { immediate: true }
)

const sectorFilterOptions = computed(() =>
  unitIds().flatMap((unitId) =>
    sectorStore
      .sectorsOf(unitId)
      .map((sector) => ({ title: catalogDisplayName(sector), value: sector.id }))
  )
)

const specialtyFilterOptions = computed(() => {
  const options = specialtyStore.optionsOf(filterStates.company ?? null)
  if (!options) return []
  return [
    ...options.official.map((o) => ({ title: `${o.name} (oficial)`, value: `o:${o.id}` })),
    ...options.company.map((o) => ({ title: o.name, value: `c:${o.id}` }))
  ]
})

/** Um select com os dois grupos: `o:<id>` → `specialtyIds`, `c:<id>` → `companySpecialtyIds`. */
const specialtyFilterValue = computed<string[]>({
  get: () => [
    ...(filterStates.specialtyIds ?? []).map((id) => `o:${id}`),
    ...(filterStates.companySpecialtyIds ?? []).map((id) => `c:${id}`)
  ],
  set: (values) => {
    const list = values ?? []
    const pick = (prefix: string) =>
      list.filter((v) => v.startsWith(prefix)).map((v) => Number(v.slice(2)))
    const official = pick('o:')
    const company = pick('c:')
    filterStates.specialtyIds = official.length ? official : undefined
    filterStates.companySpecialtyIds = company.length ? company : undefined
  }
})
</script>
